import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';
import type { Entitlements } from '@/lib/api';

/**
 * The customer's own plan and feature flags.
 *
 * Same query key as TrialBar (`['me','entitlements']`), so both share one
 * request instead of firing it twice on every page.
 *
 * `has()` reads true while the query is still loading, and on a failed
 * request. Locking a feature for the second it takes the request to land
 * would flash a lock icon or an upsell card at every paying customer on
 * every page load. The backend enforces the real gate with a 402 regardless,
 * so a customer who truly lacks the feature only sees it for that one tick.
 */
export function useEntitlements() {
  const query = useQuery({
    queryKey: ['me', 'entitlements'],
    queryFn: () => api.me.entitlements(),
    staleTime: 5 * 60_000,
    retry: false,
  });

  function has(feature: 'price_proposals' | 'dynamic_pricing'): boolean {
    if (!query.data) return true;
    return query.data.features[feature];
  }

  // For `enabled:` on a gated request. `has()` is optimistic while loading; a
  // request fired in that tick is a certain 402 for a plan without the
  // feature (#657), so requests wait for the answer. A failed answer stays
  // fail-open like `has()`: the page must not hang on data it never asks for.
  function canRequest(feature: 'price_proposals' | 'dynamic_pricing'): boolean {
    if (query.isError) return true;
    return query.data?.features[feature] === true;
  }

  return { data: query.data as Entitlements | undefined, has, canRequest, isLoading: query.isLoading };
}
